import React, { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { Link } from "react-router-dom";
import StatusPill from "@/components/StatusPill";
import ProgressRing from "@/components/ProgressRing";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { Plus, Archive, FolderKanban } from "lucide-react";

export default function Projekte() {
  const { user } = useAuth();
  const t = useT();
  const canCreate = user?.rolle === "admin" || user?.rolle === "pm";
  const [tab, setTab] = useState("aktiv");
  const [projekte, setProjekte] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/projekte?mode=${tab}`);
      setProjekte(data);
    })();
  }, [tab]);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="mono-label">{t("projekte.overview")}</div>
          <h1 className="font-archivo font-bold text-4xl mt-1">{t("projekte.title")}</h1>
        </div>
        {canCreate && (
          <Link to="/projekte/neu" data-testid="projekt-new-btn" className="inline-flex items-center gap-2 px-4 py-2.5 bg-ink text-white rounded-sm text-sm hover:bg-black">
            <Plus size={14} /> {t("projekte.new")}
          </Link>
        )}
      </div>

      <div className="flex gap-1 mb-6 border-b border-line">
        <button onClick={() => setTab("aktiv")} data-testid="tab-aktiv"
          className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${
            tab === "aktiv" ? "border-ink text-ink font-medium" : "border-transparent text-ink/50 hover:text-ink"
          }`}>
          <FolderKanban size={14} /> {t("projekte.tab_active")}
        </button>
        <button onClick={() => setTab("archiv")} data-testid="tab-archiv"
          className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${
            tab === "archiv" ? "border-ink text-ink font-medium" : "border-transparent text-ink/50 hover:text-ink"
          }`}>
          <Archive size={14} /> {t("projekte.tab_archive")}
        </button>
      </div>

      <div className="paper-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line bg-paper/60">
              <th className="w-14"></th>
              <th className="text-left mono-label py-3 px-5 font-medium">{t("col.title")}</th>
              <th className="text-left mono-label py-3 px-5 font-medium">{t("col.client")}</th>
              <th className="text-left mono-label py-3 px-5 font-medium">{t("col.designer")}</th>
              <th className="text-left mono-label py-3 px-5 font-medium">{t("col.area")}</th>
              <th className="text-left mono-label py-3 px-5 font-medium">{t("col.deadline")}</th>
              <th className="text-left mono-label py-3 px-5 font-medium">{t("col.status")}</th>
            </tr>
          </thead>
          <tbody>
            {projekte.map((p) => (
              <tr key={p.id} className="border-b border-line/60 last:border-0 hover:bg-paper/40" data-testid={`project-row-${p.id}`}>
                <td className="py-4 pl-5">
                  <ProgressRing pct={p.fortschritt_pct} size={40} stroke={4} testId={`ring-${p.id}`} />
                </td>
                <td className="py-4 px-5">
                  <Link to={`/projekte/${p.id}`} className="font-medium hover:underline">{p.titel}</Link>
                </td>
                <td className="py-4 px-5">{p.kunde_name}</td>
                <td className="py-4 px-5">{p.designer_name}</td>
                <td className="py-4 px-5"><span className="mono-label" style={{opacity: 1}}>{p.bereich}</span></td>
                <td className="py-4 px-5 font-mono text-xs">{p.laufend ? t("project.deadline_laufend") : fmtDate(p.deadline)}</td>
                <td className="py-4 px-5"><StatusPill status={p.status} /></td>
              </tr>
            ))}
            {projekte.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-ink/50">
                {tab === "archiv" ? t("projekte.no_archived") : t("projekte.no_active")}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
